import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'oro' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, className = '', children, disabled, type = 'button', ...props }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-semibold tracking-wide oro-transition focus-visible:oro-focus disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary: 'bg-accent text-black shadow-glow hover:bg-accent-light active:bg-accent-dark hover:shadow-xl',
      secondary: 'bg-surface-alt border border-border text-text-secondary shadow-soft hover:border-accent/60 hover:text-accent hover:shadow-card-soft',
      ghost: 'bg-transparent text-text-secondary hover:text-accent hover:bg-surface-alt/60',
      oro: 'bg-gradient-to-r from-accent to-accent-light text-black font-medium hover:from-accent-light hover:to-accent shadow-lg hover:shadow-xl',
      destructive: 'bg-oro-error text-oro-bg hover:bg-oro-error/90 active:bg-oro-error/80 shadow-soft'
    };

    const sizeClasses = {
      sm: 'text-xs px-3 py-1.5 rounded-md',
      md: 'text-sm px-4 py-2 rounded-md', 
      lg: 'text-base px-5 py-2.5 rounded-md'
    };

    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

    return (
      <button
        ref={ref}
        type={type}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <div className="oro-loading mr-2 h-4 w-4 rounded-full bg-current opacity-60"></div>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;